import { useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { useData } from "@/lib/useData";
import type { Stats, Activity, Deal } from "@/lib/types";
import { DEAL_STAGES } from "@/lib/types";
import { Users, Building2, DollarSign, TrendingUp, CheckCircle2, Clock } from "lucide-react";

const stageColors: Record<string, string> = {
	lead: "bg-slate-200 text-slate-800",
	qualified: "bg-blue-100 text-blue-800",
	proposal: "bg-amber-100 text-amber-800",
	negotiation: "bg-purple-100 text-purple-800",
	closed_won: "bg-green-100 text-green-800",
	closed_lost: "bg-red-100 text-red-800",
};

function formatCurrency(value: number) {
	return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(value);
}

export function DashboardPage() {
	const statsFetcher = useCallback(() => api.stats(), []);
	const dealsFetcher = useCallback(() => api.deals.list(), []);
	const activitiesFetcher = useCallback(() => api.activities.list(), []);

	const { data: stats, loading: statsLoading } = useData<Stats>(statsFetcher);
	const { data: deals } = useData<Deal[]>(dealsFetcher);
	const { data: activities } = useData<Activity[]>(activitiesFetcher);

	const recentDeals = deals?.filter(d => !["closed_won", "closed_lost"].includes(d.stage)).slice(0, 5) ?? [];
	const upcomingActivities = activities?.filter(a => !a.completed).slice(0, 5) ?? [];

	if (statsLoading || !stats) {
		return <div className="p-6 text-muted-foreground">Loading...</div>;
	}

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
				<p className="text-sm text-muted-foreground">Overview of your CRM pipeline</p>
			</div>

			{/* Stat Cards */}
			<div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
				<Card>
					<CardContent className="pt-6">
						<div className="flex items-center gap-3">
							<div className="rounded-md bg-blue-50 p-2"><Users className="h-4 w-4 text-blue-600" /></div>
							<div>
								<p className="text-2xl font-bold">{stats.totalContacts}</p>
								<p className="text-xs text-muted-foreground">Contacts</p>
							</div>
						</div>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="pt-6">
						<div className="flex items-center gap-3">
							<div className="rounded-md bg-purple-50 p-2"><Building2 className="h-4 w-4 text-purple-600" /></div>
							<div>
								<p className="text-2xl font-bold">{stats.totalCompanies}</p>
								<p className="text-xs text-muted-foreground">Companies</p>
							</div>
						</div>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="pt-6">
						<div className="flex items-center gap-3">
							<div className="rounded-md bg-amber-50 p-2"><DollarSign className="h-4 w-4 text-amber-600" /></div>
							<div>
								<p className="text-2xl font-bold">{stats.totalDeals}</p>
								<p className="text-xs text-muted-foreground">Deals</p>
							</div>
						</div>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="pt-6">
						<div className="flex items-center gap-3">
							<div className="rounded-md bg-emerald-50 p-2"><TrendingUp className="h-4 w-4 text-emerald-600" /></div>
							<div>
								<p className="text-2xl font-bold">{formatCurrency(stats.pipelineValue)}</p>
								<p className="text-xs text-muted-foreground">Pipeline</p>
							</div>
						</div>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="pt-6">
						<div className="flex items-center gap-3">
							<div className="rounded-md bg-green-50 p-2"><CheckCircle2 className="h-4 w-4 text-green-600" /></div>
							<div>
								<p className="text-2xl font-bold">{formatCurrency(stats.wonValue)}</p>
								<p className="text-xs text-muted-foreground">Won</p>
							</div>
						</div>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="pt-6">
						<div className="flex items-center gap-3">
							<div className="rounded-md bg-orange-50 p-2"><Clock className="h-4 w-4 text-orange-600" /></div>
							<div>
								<p className="text-2xl font-bold">{stats.openActivities}</p>
								<p className="text-xs text-muted-foreground">Open Tasks</p>
							</div>
						</div>
					</CardContent>
				</Card>
			</div>

			{/* Pipeline Summary */}
			<Card>
				<CardHeader>
					<CardTitle className="text-base">Pipeline by Stage</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="flex flex-wrap gap-3">
						{DEAL_STAGES.map(s => (
							<div key={s.value} className="flex items-center gap-2">
								<Badge variant="secondary" className={stageColors[s.value]}>
									{s.label}
								</Badge>
								<span className="text-sm font-medium">{stats.dealsByStage[s.value]}</span>
							</div>
						))}
					</div>
				</CardContent>
			</Card>

			<div className="grid lg:grid-cols-2 gap-6">
				{/* Active Deals */}
				<Card>
					<CardHeader>
						<CardTitle className="text-base">Active Deals</CardTitle>
					</CardHeader>
					<CardContent>
						{recentDeals.length === 0 ? (
							<p className="text-sm text-muted-foreground">No active deals</p>
						) : (
							<div className="space-y-3">
								{recentDeals.map(deal => (
									<div key={deal.id} className="flex items-center justify-between py-2 border-b last:border-0">
										<div>
											<p className="text-sm font-medium">{deal.title}</p>
											<p className="text-xs text-muted-foreground">{formatCurrency(deal.value)}</p>
										</div>
										<Badge variant="secondary" className={stageColors[deal.stage]}>
											{DEAL_STAGES.find(s => s.value === deal.stage)?.label}
										</Badge>
									</div>
								))}
							</div>
						)}
					</CardContent>
				</Card>

				{/* Upcoming Activities */}
				<Card>
					<CardHeader>
						<CardTitle className="text-base">Upcoming Activities</CardTitle>
					</CardHeader>
					<CardContent>
						{upcomingActivities.length === 0 ? (
							<p className="text-sm text-muted-foreground">No upcoming activities</p>
						) : (
							<div className="space-y-3">
								{upcomingActivities.map(activity => (
									<div key={activity.id} className="flex items-center justify-between py-2 border-b last:border-0">
										<div>
											<p className="text-sm font-medium">{activity.subject}</p>
											<p className="text-xs text-muted-foreground capitalize">{activity.type}</p>
										</div>
										{activity.dueDate && (
											<span className="text-xs text-muted-foreground">
												{new Date(activity.dueDate).toLocaleDateString()}
											</span>
										)}
									</div>
								))}
							</div>
						)}
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
